import React, { useEffect, useState } from 'react';
import { useObjectsApi } from '../../../api/objects/objects';
import { TSound } from '../../../models/objects/media';
import { Table, Button, Space, message, Input, Spin } from 'antd';
import { UploadOutlined, EditOutlined, SaveOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import { getSoundPath } from '../../../utils/format/valueFormat';

const SoundList: React.FC = () => {
    const { getSounds, createSound, updateSound, deleteSound, uploadSound } = useObjectsApi();
    const [sounds, setSounds] = useState<TSound[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [editingKey, setEditingKey] = useState<number | null>(null);
    const [editingSound, setEditingSound] = useState<TSound | null>(null);
    const [loadingRow, setLoadingRow] = useState<number | null>(null);
    const [addingId, setAddingId] = useState<number>(0);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [reloadFlag, setReloadFlag] = useState(0);

    useEffect(() => {
        setLoading(true);
        getSounds()
            .then(resp => {
                // Garantit que resp.data est un tableau
                const list = Array.isArray(resp.data) ? resp.data : [];
                setSounds(list);
            })
            .catch(err => message.error(err.response?.data || err.message))
            .finally(() => setLoading(false));
    // eslint-disable-next-line
    }, [reloadFlag]);

    const handleAdd = () => {
        const newId = addingId - 1;
        const newSound: TSound = {
            id: newId,
            file_name: '',
            file_path: '',
        };
        setAddingId(newId);
        setEditingKey(newId);
        setEditingSound(newSound);

        // Mise à jour fonctionnelle et protection contre un état non itérable
        setSounds(prev => Array.isArray(prev) ? [newSound, ...prev] : [newSound]);
    };

    const edit = (record: TSound) => {
        setEditingKey(record.id);
        setEditingSound({ ...record });
    };

    const cancel = () => {
        if (editingSound && editingSound.id < 0) {
            setSounds(sounds.filter(snd => snd.id !== editingSound.id));
        }
        setEditingKey(null);
        setEditingSound(null);
    };

    const save = (id: number) => {
        if (!editingSound) return;
        setLoadingRow(id);
        const action = editingSound.id > 0
            ? updateSound(editingSound.id, editingSound)
            : createSound(editingSound);
        action
            .then(resp => {
                const updatedSound = resp.data;
                setSounds(sounds.map(snd => snd.id === id ? updatedSound : snd));
                message.success('Enregistré avec succès');
            })
            .catch(err => {
                message.error(err.response?.data || err.message);
            })
            .finally(() => {
                setLoadingRow(null);
                setEditingKey(null);
                setEditingSound(null);
            });
    };

    const handleDelete = (id: number) => {
        setDeleteLoading(true);
        deleteSound(id)
            .then(() => {
                setSounds(sounds.filter(snd => snd.id !== id));
                message.success('Supprimé avec succès');
            })
            .catch(err => {
                message.error(err.response?.data || err.message);
            })
            .finally(() => setDeleteLoading(false));
    };

    const uploadFile = (record: TSound) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'audio/*';
        input.onchange = (e: any) => {
            const file = e.target.files[0];
            if (file) {
                setLoadingRow(record.id);
                const formData = new FormData();
                formData.append('sound', file);
                uploadSound(record.id, formData)
                    .then(resp => {
                        const updatedSound = resp.data;
                        setSounds(sounds.map(snd => snd.id === record.id ? updatedSound : snd));
                        setReloadFlag(prev => prev + 1); // relance le fetch si besoin
                        message.success('Son uploadé avec succès');
                    })
                    .catch(err => {
                        message.error(err.response?.data || err.message);
                    })
                    .finally(() => setLoadingRow(null));
            }
        };
        input.click();
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Nom du fichier',
            dataIndex: 'file_name',
            key: 'file_name',
            render: (text: string, record: TSound) =>
                editingKey === record.id ? (
                    <Input
                        value={editingSound?.file_name}
                        onChange={(e) =>
                            setEditingSound(editingSound ? { ...editingSound, file_name: e.target.value } : null)
                        }
                    />
                ) : (
                    text
                ),
        },
        {
            title: 'Audio',
            dataIndex: 'file_path',
            key: 'audio',
            render: (file_path: string) => {
                const audioUrl = getSoundPath(file_path);
                return (
                    <audio controls>
                        <source src={audioUrl} type="audio/mpeg" />
                        Votre navigateur ne supporte pas l'élément audio.
                    </audio>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: TSound) => (
                <Space size="middle">
                    {loadingRow === record.id ? (
                        <Spin />
                    ) : editingKey === record.id ? (
                        <>
                            <Button icon={<SaveOutlined />} onClick={() => save(record.id)} />
                            <Button icon={<StopOutlined />} onClick={cancel} />
                        </>
                    ) : (
                        <>
                            <Button icon={<EditOutlined />} onClick={() => edit(record)} />
                            <Button icon={<UploadOutlined />} onClick={() => uploadFile(record)} />
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => handleDelete(record.id)}
                                loading={deleteLoading}
                            />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={handleAdd}>
                    Ajouter un son
                </Button>
            </Space>
            <Table
                dataSource={sounds}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 20 }}
            />
        </div>
    );
};

export default SoundList;
